import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  COMMUNICATION_GRAPH_DRAFT_MESSAGE,
  type CommunicationGraphDraftRequest,
  type CommunicationGraphDraftResult
} from '../../../../src/contracts/communicationGraphDraftContracts';
import { acquireGraphFeatureTokenInteractively, acquireGraphFeatureTokenSilently } from '../services/graph/interactiveGraphFeatureClient';
import { createOutlookGraphDraft } from '../services/graph/outlookGraphDraftService';
import { usePortalContext } from '../context/PortalContext';
import { resolveGraphDraftRecoveryActions } from '../services/graph/graphDraftRecovery';

type FeatureConfig = { tenantId: string; clientId: string; scopes: string[] };

const isAllowedOpenerOrigin = (value: string): boolean => {
  try {
    const url = new URL(value);
    return (url.protocol === 'https:' && url.hostname.endsWith('.sharepoint.com')) ||
      ((url.protocol === 'http:' || url.protocol === 'https:') && url.hostname === 'localhost');
  } catch {
    return false;
  }
};

const buildMailto = (request: CommunicationGraphDraftRequest): string => {
  const params = new URLSearchParams({ subject: request.subject, body: request.plainText });
  return `mailto:${encodeURIComponent(request.recipients.join(','))}?${params.toString()}`;
};

const CommunicationCompose = (): JSX.Element => {
  const { tenant, loading } = usePortalContext();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const nonce = params.get('nonce') ?? '';
  const openerOrigin = params.get('openerOrigin') ?? '';
  const [request, setRequest] = useState<CommunicationGraphDraftRequest>();
  const [config, setConfig] = useState<FeatureConfig>();
  const [state, setState] = useState<'loading' | 'consent' | 'preparing' | 'prepared' | 'error'>('loading');
  const [error, setError] = useState<string>();
  const portalTenantId = tenant?.tenantId;

  const report = useCallback((status: CommunicationGraphDraftResult['status'], message?: string): void => {
    if (!window.opener || !isAllowedOpenerOrigin(openerOrigin)) return;
    window.opener.postMessage({ kind: COMMUNICATION_GRAPH_DRAFT_MESSAGE, phase: 'result', nonce, status, error: message }, openerOrigin);
  }, [nonce, openerOrigin]);

  useEffect(() => {
    if (loading || !portalTenantId || !nonce || !window.opener || !isAllowedOpenerOrigin(openerOrigin)) return;
    let disposed = false;
    let readyTimer: number | undefined;
    const receive = (event: MessageEvent): void => {
      const candidate = event.data as Partial<CommunicationGraphDraftRequest>;
      if (event.source !== window.opener || event.origin !== openerOrigin || candidate.kind !== COMMUNICATION_GRAPH_DRAFT_MESSAGE || candidate.phase !== 'request' || candidate.nonce !== nonce) return;
      if (candidate.tenantId?.toLowerCase() !== portalTenantId.toLowerCase()) {
        setError('This draft belongs to a different Microsoft 365 tenant. Return to Edinshall Audit and try again.');
        setState('error');
        report('failed', 'Tenant validation failed.');
        return;
      }
      if (readyTimer !== undefined) window.clearInterval(readyTimer);
      setRequest(candidate as CommunicationGraphDraftRequest);
    };
    window.addEventListener('message', receive);
    fetch('/api/communication/graph-draft/config')
      .then(async response => {
        if (!response.ok) throw new Error('Rich Outlook drafts are not currently active for this customer.');
        return response.json() as Promise<FeatureConfig>;
      })
      .then(value => {
        if (disposed) return;
        setConfig(value);
        const postReady = (): void => window.opener?.postMessage({ kind: COMMUNICATION_GRAPH_DRAFT_MESSAGE, phase: 'ready', nonce }, openerOrigin);
        postReady();
        readyTimer = window.setInterval(postReady, 750);
      })
      .catch(reason => {
        if (disposed) return;
        setError((reason as Error).message);
        setState('error');
      });
    return () => {
      disposed = true;
      if (readyTimer !== undefined) window.clearInterval(readyTimer);
      window.removeEventListener('message', receive);
    };
  }, [loading, nonce, openerOrigin, portalTenantId, report]);

  const prepare = useCallback(async (token: string): Promise<void> => {
    if (!request) return;
    setState('preparing');
    setError(undefined);
    try {
      const draft = await createOutlookGraphDraft({ token, ...request });
      setState('prepared');
      report('prepared');
      window.opener = null;
      window.location.assign(draft.webLink);
    } catch (reason) {
      setError((reason as Error).message);
      setState('error');
    }
  }, [report, request]);

  useEffect(() => {
    if (!request || !config || state !== 'loading') return;
    acquireGraphFeatureTokenSilently({ clientId: config.clientId, tenantId: config.tenantId, scopes: config.scopes, loginHint: tenant?.userEmail })
      .then(token => token ? prepare(token) : setState('consent'))
      .catch(() => setState('consent'));
  }, [config, prepare, request, state, tenant?.userEmail]);

  const connect = async (): Promise<void> => {
    if (!config) return;
    try {
      const token = await acquireGraphFeatureTokenInteractively({ clientId: config.clientId, tenantId: config.tenantId, scopes: config.scopes });
      await prepare(token);
    } catch (reason) {
      setError(`Microsoft permission was not granted: ${(reason as Error).message}`);
      setState('error');
    }
  };

  const fallback = (): void => {
    if (!request) return;
    report('fallback');
    window.opener = null;
    window.location.href = buildMailto(request);
  };

  const adminApprovalUrl = config
    ? `https://login.microsoftonline.com/${encodeURIComponent(config.tenantId)}/adminconsent?client_id=${encodeURIComponent(config.clientId)}&redirect_uri=${encodeURIComponent(`${window.location.origin}/auth-redirect.html`)}`
    : '';
  const recovery = resolveGraphDraftRecoveryActions({ hasFeatureConfig: Boolean(config), hasDraftRequest: Boolean(request) });

  return <div className="card" style={{ maxWidth: '760px', margin: '32px auto' }}>
    <h2>Prepare rich Outlook draft</h2>
    <p className="muted">Edinshall creates a draft in your own mailbox. It does not send the email. You review, edit and send it yourself in Outlook.</p>
    {state === 'loading' && <p>Loading the draft request…</p>}
    {state === 'consent' && <>
      <p>This optional feature needs delegated <code>Mail.ReadWrite</code> so it can create this draft and add your selected attachments. Core Edinshall use does not need this permission.</p>
      <button className="btn primary" type="button" onClick={() => void connect()}>Connect to Microsoft Outlook</button>
      <button className="btn secondary" type="button" onClick={fallback}>Use standard email instead</button>
    </>}
    {state === 'preparing' && <p>Creating the Outlook draft and adding your selected attachments…</p>}
    {state === 'prepared' && <>
      <p className="pill success">Draft prepared — not sent</p>
      <p className="muted">Outlook has opened for you to review the draft. You can now close this window.</p>
    </>}
    {state === 'error' && <>
      <p className="error-text" role="alert">{error}</p>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {recovery.retry && <button className="btn primary" type="button" onClick={() => void connect()}>Retry Outlook connection</button>}
        {recovery.standardEmailFallback && <button className="btn secondary" type="button" onClick={fallback}>Use standard email instead</button>}
      </div>
      {recovery.adminApproval && adminApprovalUrl && <p className="muted">If your organisation blocks user consent, copy this administrator approval link to your IT team:<br /><input readOnly value={adminApprovalUrl} style={{ width: '100%' }} /></p>}
    </>}
  </div>;
};

export default CommunicationCompose;
